import type {
  AssignmentSortOptions,
  EventSessionAttendee,
  EventSessionFilterOptions,
  Event,
  NextPathModuleMetadata,
  TrainingClassSession,
} from '../types';
import _ from 'underscore';
import XDate from 'xdate';
import EventAttendeeRegistrationStatus from '~/event-attendee-registration-status';
import SeriesUtils from '~/common/series-utils';

const { APP_CONSTANTS } = window;

export function getUpcomingEventSessionAttendees(
  eventSessionAttendees: EventSessionAttendee[],
): EventSessionAttendee[] {
  const currentTime = Date.now();
  return eventSessionAttendees.filter(({ trainingClassSession }) => trainingClassSession.end >= currentTime);
}

export function getPastEventSessionAttendees(eventSessionAttendees: EventSessionAttendee[]): EventSessionAttendee[] {
  const currentTime = Date.now();
  return eventSessionAttendees.filter((eventSessionAttendee) => {
    const learnerRegistered = new EventAttendeeRegistrationStatus(eventSessionAttendee.status).isRegistered();
    const learnerRegisteredOrAttended = eventSessionAttendee.didAttend || learnerRegistered;

    return learnerRegisteredOrAttended && eventSessionAttendee.trainingClassSession.end <= currentTime;
  });
}

export function sortEventSessionAttendees(
  sortBy: AssignmentSortOptions,
  eventSessionAttendees: EventSessionAttendee[],
): EventSessionAttendee[] {
  switch (sortBy) {
    case 'date':
      return _.sortBy(eventSessionAttendees, (sessionAttendee) => sessionAttendee.trainingClassSession.start);

    case 'name': {
      const sortedBySessionName = _.sortBy(
        eventSessionAttendees,
        (sessionAttendee) => sessionAttendee.trainingClassSession.title,
      );

      return _.sortBy(
        sortedBySessionName,
        (sessionAttendee) => sessionAttendee.trainingClassSession.trainingClass.name,
      );
    }

    default:
      return eventSessionAttendees;
  }
}

export function filterEventSessionAttendees(
  filterBy: EventSessionFilterOptions,
  eventSessionAttendees: EventSessionAttendee[],
): EventSessionAttendee[] {
  switch (filterBy) {
    case 'view_all':
      return getUpcomingEventSessionAttendees(eventSessionAttendees);
    case 'past_events':
      return getPastEventSessionAttendees(eventSessionAttendees);

    default:
      return eventSessionAttendees;
  }
}

export const getNextPathModuleUrl = (nextPathModuleMetadata: NextPathModuleMetadata) => {
  if (!_.isEmpty(nextPathModuleMetadata)) {
    return SeriesUtils.endUserAssignmentUrl(
      nextPathModuleMetadata.next_module_type,
      nextPathModuleMetadata.next_module_assignment_id,
      nextPathModuleMetadata.next_module_id,
      nextPathModuleMetadata.training_series_user_assignment_id,
      nextPathModuleMetadata.parent_assignment_ids,
    );
  }

  return null;
};

export const sessionHasEnded = (session: TrainingClassSession) => {
  if (session.end == null) return false;

  const sessionEndDate = new XDate(session.end);
  const currentDate = new XDate();

  return sessionEndDate < currentDate;
};

export const getLearnerIsRegisteredAttendee = (learnerId: number, attendee: EventSessionAttendee) => {
  const learnerIsAttendee = attendee.user.id === learnerId;
  const attendeeIsRegistered = new EventAttendeeRegistrationStatus(attendee.status).isRegistered();

  return learnerIsAttendee && attendeeIsRegistered;
};

export const getIsLearnerRegisteredForSession = (learnerId: number, attendees: EventSessionAttendee[]) =>
  attendees?.some((attendee) => getLearnerIsRegisteredAttendee(learnerId, attendee));

const getIsLearnerWaitlistedAttendee = (learnerId: number, attendee: EventSessionAttendee) => {
  const learnerIsAttendee = attendee.user.id === learnerId;
  const attendeeIsWaitlisted = new EventAttendeeRegistrationStatus(attendee.status).isWaitlisted();

  return learnerIsAttendee && attendeeIsWaitlisted;
};

export const getIsLearnerRegisteredForEventSeries = (learnerId: number, event: Event) =>
  event.trainingClassSessions.some((session) =>
    session.trainingClassSessionAttendees.some((attendee) => getLearnerIsRegisteredAttendee(learnerId, attendee)),
  );

export const getIsLearnerWaitlisted = (learnerId: number, attendees: EventSessionAttendee[]) =>
  attendees?.some((attendee) => getIsLearnerWaitlistedAttendee(learnerId, attendee));

export const getIsLearnerWaitlistedForEventSeries = (learnerId: number, event: Event) =>
  event.trainingClassSessions.some((session) =>
    session.trainingClassSessionAttendees.some((attendee) => getIsLearnerWaitlistedAttendee(learnerId, attendee)),
  );

export const getIsLearnerAttendedSession = (learnerId: number, attendees: EventSessionAttendee[]) =>
  attendees?.some((attendee) => {
    const learnerIsAttendee = attendee.user.id === learnerId;
    const attendeeDidAttend = attendee.didAttend;
    return learnerIsAttendee && attendeeDidAttend;
  });

export const getIsLearnerAbsentSession = (learnerId: number, attendees: EventSessionAttendee[]) =>
  attendees?.some((attendee) => {
    const learnerIsAttendee = attendee.user.id === learnerId;
    const attendeeIsAbsent = attendee.status === APP_CONSTANTS.EVENT_ATTENDEE_STATUSES.absent;
    return learnerIsAttendee && attendeeIsAbsent;
  });

export const getIsSessionFull = (session: TrainingClassSession, maxAttendees: number) =>
  maxAttendees !== null && typeof maxAttendees !== 'undefined' && session.numAttendees >= maxAttendees;

export const getCanOnlyWaitlistEvent = (event: Event) =>
  event.waitlistBehavior === APP_CONSTANTS.INTERNAL_EVENT_WAITLIST_BEHAVIORS.WAITLIST_FIRST_MANUAL_WAITLIST.KEY;

export const getIsWaitlistFull = (event: Event, session: TrainingClassSession) => {
  if (!getIsEventWaitlistLimitEnabled(event)) {
    return false;
  }

  return session.numWaitlistedAttendees >= event.maxWaitlistedAttendees;
};

export const getIsEventWaitlistLimitEnabled = (event: Event) =>
  event.waitlistEnabled && event.maxWaitlistedAttendees != null;

export const getIsDeadlinePast = (event: Event, session: TrainingClassSession) => {
  if (!event.numDaysPriorToCloseRegistration) {
    return false;
  }
  const sessionStartDate = new XDate(session.start);
  const cutoffDate = sessionStartDate.addDays(-event.numDaysPriorToCloseRegistration);

  return new XDate() > cutoffDate;
};
